import { useTranslation } from 'react-i18next';
import { ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from 'recharts';
import { GlassCard } from '@/components/glass';
import type { TreatmentBreakdownItem } from '../types';

const COLORS = ['var(--color-primary-800)', '#0097a7', '#43a047', '#ef6c00'];

interface TreatmentBreakdownChartProps {
  data: TreatmentBreakdownItem[];
}

export function TreatmentBreakdownChart({ data }: TreatmentBreakdownChartProps) {
  const { t, i18n } = useTranslation();
  const isHe = i18n.language === 'he';

  const chartData = data.map((item) => ({
    ...item,
    name: item.type
      ? t(`treatments.${item.type}`, item.type)
      : (isHe ? item.name : item.nameEn),
  }));

  if (chartData.length === 0) {
    return (
      <GlassCard title={t('dashboard.treatmentBreakdown')} padding="none">
        <div className="flex h-48 items-center justify-center text-sm text-slate-500">
          {t('common.noData')}
        </div>
      </GlassCard>
    );
  }

  return (
    <GlassCard title={t('dashboard.treatmentBreakdown')} padding="compact" contentClassName="h-52">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={chartData}
            cx="50%"
            cy="42%"
            innerRadius={35}
            outerRadius={60}
            paddingAngle={3}
            dataKey="count"
          >
            {chartData.map((_entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} />
            ))}
          </Pie>
          <Tooltip
            contentStyle={{ borderRadius: 8, fontSize: 12 }}
            formatter={(value: number, _name: string, entry) => [
              `${value}`,
              entry.payload.name,
            ]}
          />
          <Legend
            wrapperStyle={{ fontSize: 11 }}
            formatter={(value, entry) => {
              const item = chartData.find(d => d.name === value);
              return `${value} (${item?.count ?? (entry as any).payload?.count ?? ''})`;
            }}
          />
        </PieChart>
      </ResponsiveContainer>
    </GlassCard>
  );
}
